import type { Template } from '@cognite/apm-client';
import { EmptyState, FindIllustration, Skeleton } from '@cognite/cogs.js-v10';
import { FETCH_MODE } from '@infield/constants/common';
import { SearchResultHighlight } from '@infield/features/checklist/checklist-item';
import { ComponentScrollableContent } from '@infield/features/ui';
import { t } from 'i18next';
import type { FC } from 'react';

import * as S from './elements';

type TemplateListUiProps = {
  isLoading: boolean;
  templates: Template[];
  fetchMode: string;
  debouncedQuery: string;
  selectedTemplate: Template | undefined;
  handleSelectTemplate: (template: Template) => void;
};

export const TemplateListUi: FC<TemplateListUiProps> = ({
  isLoading,
  templates,
  fetchMode,
  debouncedQuery,
  selectedTemplate,
  handleSelectTemplate,
}) => {
  if (isLoading) {
    return <Skeleton.List lines={4} borders />;
  }

  if (!templates || templates.length === 0) {
    return (
      <ComponentScrollableContent>
        <S.EmptyStateContainer>
          <EmptyState
            size="small"
            title={t('TEMPLATE_LIST_WARNING_NO_TEMPLATES_TITLE', 'No results')}
            description={
              fetchMode === FETCH_MODE.SEARCH
                ? t(
                    'TEMPLATE_LIST_WARNING_NO_TEMPLATES_ON_SEARCH_TEXT',
                    'Try adjusting your search query or filters'
                  )
                : t(
                    'TEMPLATE_LIST_WARNING_NO_TEMPLATES',
                    'No templates available'
                  )
            }
            illustration={<FindIllustration />}
          />
        </S.EmptyStateContainer>
      </ComponentScrollableContent>
    );
  }

  return (
    <ComponentScrollableContent>
      <S.TemplateList data-testid="template-list">
        {templates.map((template) => (
          <S.TemplateListItem
            data-testid="template-list-item"
            $isSelected={selectedTemplate?.externalId === template.externalId}
            key={template.externalId}
            onClick={() => handleSelectTemplate(template)}
          >
            <S.TemplateListItemTitle>
              <SearchResultHighlight
                text={template.title || ''}
                searchQuery={debouncedQuery}
              />
            </S.TemplateListItemTitle>
          </S.TemplateListItem>
        ))}
      </S.TemplateList>
    </ComponentScrollableContent>
  );
};
